import { useEffect } from 'react'
import { createBrowserRouter, Outlet, redirect, RouterProvider } from 'react-router-dom'
import { Header } from './components/Header'
import { UserInfoPage } from './components/pages/userInfo/UserInfoPage'
import { UsersPage } from './components/pages/UsersPage'
import { consumerSessionLoader, ConsumerSessionPage } from './components/pages/ConsumerSessionPage'
import { PayoutPage } from './components/pages/PayoutPage'
import { BagPage, bagtagLoader } from './components/pages/BagPage'
import { BagsPage } from './components/pages/bags/BagsPage'
import { SearchPage } from './components/pages/SearchPage'
import { hasExternalLocationWritePermission, hasMytomraBackofficePermission } from './services/permission.service'
import { ExternalLocationsPage } from './components/pages/external-locations/ExternalLocationsPage'
import { ErrorBoundary } from 'react-error-boundary'
import { GeneralErrorBoundary, onGeneralErrorBoundary, RouterErrorBoundary } from './components/ErrorPage'
import { initDatadog } from '@tomra/datadog-browser-logging'
import { DATADOG_CONFIG, ENVIRONMENT, STAGE, VERSION } from './lib'

const AuthenticatedAppLayout = () => {
  return (
    <>
      <Header />
      <div className="mx-auto max-w-6xl px-4">
        <Outlet />
      </div>
    </>
  )
}

export const App = () => {
  const backofficeRoutes = hasMytomraBackofficePermission()
    ? [
        {
          path: 'user',
          element: <Outlet />,
          children: [
            {
              path: ':userId',
              element: <UserInfoPage />
            }
          ]
        },
        {
          path: 'users',
          element: <Outlet />,
          children: [
            {
              path: ':query',
              element: <UsersPage />
            }
          ]
        },
        {
          path: 'consumersession/:dpuid',
          loader: consumerSessionLoader,
          element: <ConsumerSessionPage />
        },
        {
          path: 'payout',
          element: <Outlet />,
          children: [
            {
              path: ':payoutId',
              element: <PayoutPage />
            }
          ]
        },
        {
          path: 'bagtag',
          element: <Outlet />,
          children: [
            {
              path: ':bagtag',
              loader: bagtagLoader,
              element: <BagPage />
            }
          ]
        },
        { path: 'bags', element: <BagsPage /> },
        { path: 'search', element: <SearchPage /> }
      ]
    : []

  const externalLocationsRoutes = hasExternalLocationWritePermission()
    ? [
        {
          path: 'external-locations',
          element: <ExternalLocationsPage />
        }
      ]
    : []

  const authenticatedRoutes = [
    {
      element: <AuthenticatedAppLayout />,
      errorElement: <RouterErrorBoundary />,
      children: [
        ...backofficeRoutes,
        ...externalLocationsRoutes,
        {
          path: 'search',
          element: <SearchPage />
        },
        {
          path: '*',
          loader: () => redirect('/search')
        }
      ]
    }
  ]

  useEffect(() => {
    initDatadog({ ...DATADOG_CONFIG, version: VERSION, env: `${ENVIRONMENT}.${STAGE}` })
  }, [])

  return (
    <ErrorBoundary fallbackRender={GeneralErrorBoundary} onError={onGeneralErrorBoundary}>
      <RouterProvider router={createBrowserRouter(authenticatedRoutes)} />
    </ErrorBoundary>
  )
}
